import classNames from 'classnames';
import React, {ReactElement, useEffect, useState} from 'react';

import Col from '../../common/Col';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {useAppDispatch} from '../../store';
import {Actions} from '../gameSlice';
import {useGameStatus} from '../hooks';
import {computeCost, computeOption} from '../mechanics';
import {QuestionId} from '../resources/questions';
import {Question} from '../types';
import {FeedbackButton} from './buttons/PillButtons';
import Choice from './Choice';
import HelpMenu from './HelpMenu';
import MessageBox from './MessageBox';
import {SimplePill} from './SimplePill';
import Toolbox from './Toolbox';

type Props = {
  question: Question;
};

const Question = ({question}: Props): ReactElement => {
  const dispatch = useAppDispatch();
  const gameStatus = useGameStatus();
  const {isMobile} = useBreakpoint('mobile');
  const [currentQuestion, setCurrentQuestion] = useState<QuestionId>();
  const [selectedOption, setSelectedOption] = useState<number>(-1);

  useEffect(() => {
    if (question.id !== currentQuestion) {
      setCurrentQuestion(question.id);
      setSelectedOption(-1);
    }
  }, [question.id, currentQuestion]);

  return (
    <MessageBox
      top={question.heading && <SimplePill>{question.heading}</SimplePill>}
      topRight={
        isMobile && (
          <Col className="space-y-sm">
            <FeedbackButton />
            <HelpMenu />
            <Toolbox />
          </Col>
        )
      }
      bottomIsTall
      bottom={
        <div
          className={classNames(
            'pb-32',
            isMobile
              ? 'flex flex-col space-y-8 mx-20'
              : classNames(
                  'grid grid-flow-row gap-x-md gap-y-md justify-items-center select-none',
                  question.options.length === 1 ? 'w-1/2 m-auto' : 'mx-lg',
                  [2, 4].includes(question.options.length) && 'grid-cols-2',
                  question.options.length === 3 && 'grid-cols-3 -mx-[22rem]'
                )
          )}
        >
          {question.options.map((option, idx) => {
            const computedOption = computeOption(gameStatus, question.id, option.id);
            if (!computedOption) return null;
            const computedCost = computedOption.cost ?? computeCost(gameStatus, computedOption.costPerHa);
            return (
              <Choice
                key={option.id}
                title={computedOption.title}
                description={computedOption.subtitle}
                onClick={() => setSelectedOption(idx)}
                onConfirm={() => dispatch(Actions.executeOption({questionId: question.id, optionId: option.id}))}
                selected={question.options.length === 1 || selectedOption === idx}
                cost={computedCost}
                costPerHa={computedOption.costPerHa}
                canPay={true}
                style={{width: '100%'}}
              />
            );
          })}
        </div>
      }
    >
      <div className="text-center mt-sm mx-md min-h-[15rem] mobile:mx-0">
        <div className="mb-4 text-xl font-semibold leading-tight mobile:text-lg">{question.subtitle}</div>
        <div className="text-huge font-semibold leading-tight mobile:text-3xl">{question.title}</div>
      </div>
    </MessageBox>
  );
};

export default Question;
